/**
 * Type-safe URL builder for the routes declared in `routePaths.ts`.
 *
 *   buildPath(paths.JOBS_EDITOR, { campaign_id: 'job-42' }, { tab: 'sequence' })
 *   // → "/scripts/script-editor/job-42?tab=sequence"
 *
 * `PathParams` extracts `:param` names from the template at compile time, so
 * forgetting a required segment or passing an unknown key is a type error.
 * Query values are pragmatically typed — the registry knows which params
 * each page accepts, but enforcing that at the type level would require
 * mapping every template to its registry entry, which we don't need yet.
 */

// Walk the template right-to-left, accumulating placeholder names.
// `${string}:${infer P}/${infer R}` matches one placeholder followed by more
// segments; `${string}:${infer P}` matches the trailing placeholder (no
// further '/'). Templates with no placeholders fall through to the empty
// object, so `buildPath('/scripts')` requires no params at all.
export type PathParams<S extends string> =
  S extends `${string}:${infer P}/${infer R}`
    ? { [K in P | keyof PathParams<`/${R}`>]: string }
    : S extends `${string}:${infer P}`
      ? { [K in P]: string }
      : Record<string, never>;

export type QueryValue =
  | string
  | number
  | boolean
  | string[]
  | null
  | undefined;
export type QueryRecord = Record<string, QueryValue>;

/**
 * Convert a `URLSearchParams` (or any string-pair iterable) into a
 * `QueryRecord` that `useAppNavigate` / `buildPath` accept, preserving
 * duplicate keys as arrays.
 *
 * The naive shortcut `Object.fromEntries(searchParams)` silently drops all
 * but the last value for repeated keys (e.g. `?status=PENDING&status=PAUSED`
 * collapses to just `PAUSED`), which would silently strip multi-select
 * filter state on navigation. This helper preserves them so the resulting
 * `QueryRecord` round-trips through `appendQuery` losslessly.
 */
export function searchParamsToQueryRecord(
  params: Iterable<[string, string]>
): QueryRecord {
  const record: QueryRecord = {};
  for (const [key, value] of params) {
    const existing = record[key];
    if (existing === undefined) {
      record[key] = value;
    } else if (Array.isArray(existing)) {
      existing.push(value);
    } else if (typeof existing === 'string') {
      record[key] = [existing, value];
    }
    // Other QueryValue shapes can't appear from URLSearchParams, so we leave
    // them alone.
  }
  return record;
}

/** `query` as a `?a=b` search string, or `''` when it has no values. */
export function buildSearch(query: QueryRecord | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === null || item === undefined || item === '') continue;
        params.append(key, String(item));
      }
    } else {
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

function appendQuery(path: string, query: QueryRecord | undefined): string {
  return `${path}${buildSearch(query)}`;
}

function substituteParams(
  template: string,
  params: Readonly<Record<string, string>> | undefined
): string {
  // `:param` segments must occupy a whole path segment, so anchor the
  // replacement on a leading `/` (or string start) and a trailing `/` (or
  // string end). Prevents an accidental substring match if a param name is
  // ever a prefix of another (none today, but cheap to be defensive).
  let out = template;
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      const pattern = new RegExp(`(^|/):${key}(?=/|$)`, 'g');
      out = out.replace(
        pattern,
        (_match, leading) => `${leading}${encodeURIComponent(value)}`
      );
    }
  }
  // Defensive backstop for callers that bypass TypeScript (e.g. JS callers,
  // dynamic templates). PathParams<P> already makes the missing-key case a
  // compile error; this turns it into a loud runtime error instead of a
  // broken URL with a literal `:param` segment.
  const unreplaced = out.match(/(^|\/):[A-Za-z_][A-Za-z0-9_]*(?=\/|$)/);
  if (unreplaced) {
    throw new Error(
      `buildPath: template ${JSON.stringify(template)} is missing a value for ${unreplaced[0].replace(/^\//, '')}`
    );
  }
  return out;
}

// Paths without any `:param` placeholders accept either an empty-object
// params argument or none at all; paths with placeholders require the
// matching keys at compile time.
export type NoParams<P extends string> =
  PathParams<P> extends Record<string, never> ? true : false;

export function buildPath<P extends string>(
  template: P,
  ...rest: NoParams<P> extends true
    ? [params?: PathParams<P>, query?: QueryRecord]
    : [params: PathParams<P>, query?: QueryRecord]
): string;
export function buildPath(
  template: string,
  params?: Readonly<Record<string, string>>,
  query?: QueryRecord
): string {
  return buildRuntimePath(template, params, query);
}

/**
 * `buildPath` without the compile-time `:param` check, for the navigation
 * hooks whose own signatures have already enforced it. A missing param still
 * throws at runtime.
 */
export function buildRuntimePath(
  template: string,
  params: Readonly<Record<string, string>> | undefined,
  query?: QueryRecord
): string {
  return appendQuery(substituteParams(template, params), query);
}
